import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Download, Upload } from "lucide-react";
import type { EquipmentImportPreview, EquipmentImportResult } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  confirmEquipmentImport,
  downloadEquipmentImportTemplate,
  previewEquipmentImport,
} from "./api";
import { useEquipmentPermissions } from "./use-equipment-permissions";

type Step = "upload" | "preview" | "done";

export function EquipmentImportPage() {
  const navigate = useNavigate();
  const { canWrite } = useEquipmentPermissions();
  const [csv, setCsv] = useState("");
  const [step, setStep] = useState<Step>("upload");
  const [preview, setPreview] = useState<EquipmentImportPreview | null>(null);
  const [result, setResult] = useState<EquipmentImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!canWrite) {
    return (
      <PageHeader title="Access denied" description="You do not have permission to import equipment." />
    );
  }

  const handleFile = (file: File) => {
    void file.text().then((text) => {
      setCsv(text);
      setPreview(null);
      setResult(null);
      setStep("upload");
      setError(null);
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadEquipmentImportTemplate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download template");
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await previewEquipmentImport(csv);
      setPreview(res.data);
      setStep("preview");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview) return;
    const rows = preview.rows.filter((row) => row.canImport).map((row) => row.parsed!);
    if (rows.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      const res = await confirmEquipmentImport(rows);
      setResult(res.data);
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title="Import equipment"
        description="Download the CSV template, upload your fleet roster, preview validation, then confirm the import."
      />

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" type="button" onClick={() => void handleDownloadTemplate()}>
          <Download className="mr-1.5 h-4 w-4" />
          Download template
        </Button>
        <label className="inline-flex cursor-pointer items-center rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted">
          <Upload className="mr-1.5 h-4 w-4" />
          Upload CSV
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
        </label>
      </div>

      {step !== "done" ? (
        <textarea
          value={csv}
          onChange={(e) => {
            setCsv(e.target.value);
            setPreview(null);
            setStep("upload");
          }}
          rows={10}
          placeholder="Paste CSV content or upload a file…"
          className="w-full rounded-xl border border-input bg-card-white p-4 font-mono text-xs"
        />
      ) : null}

      {error ? <p className="text-sm text-red-danger">{error}</p> : null}

      {step === "upload" ? (
        <div className="flex gap-2">
          <Button onClick={() => void handlePreview()} disabled={!csv.trim() || loading}>
            {loading ? "Validating…" : "Preview import"}
          </Button>
          <Link to="/equipment">
            <Button variant="outline">Cancel</Button>
          </Link>
        </div>
      ) : null}

      {preview && step === "preview" ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <SummaryCard label="Rows" value={preview.summary.total} />
            <SummaryCard label="Ready" value={preview.summary.ready} tone="success" />
            <SummaryCard label="Blocked" value={preview.summary.blocked} tone="danger" />
            <SummaryCard label="Warnings" value={preview.summary.warningCount} tone="warning" />
          </div>

          <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="border-b bg-bg-light text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3">Row</th>
                    <th className="px-4 py-3">Equipment ID</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Site</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr
                      key={row.row}
                      className={cn(
                        "border-b border-border/60 last:border-0",
                        row.canImport ? "bg-white" : "bg-red-50/40",
                      )}
                    >
                      <td className="px-4 py-3 tabular-nums">{row.row}</td>
                      <td className="px-4 py-3 font-mono">{row.equipmentId || "—"}</td>
                      <td className="px-4 py-3">{row.name || "—"}</td>
                      <td className="px-4 py-3">{row.type || "—"}</td>
                      <td className="px-4 py-3">{row.siteCode || "—"}</td>
                      <td className="px-4 py-3">{row.status || "—"}</td>
                      <td className="px-4 py-3">
                        <IssueList errors={row.errors} warnings={row.warnings} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => void handleConfirmImport()}
              disabled={preview.summary.ready === 0 || loading}
            >
              {loading ? "Importing…" : `Confirm import (${preview.summary.ready})`}
            </Button>
            <Button variant="outline" onClick={() => { setStep("upload"); }}>
              Back
            </Button>
          </div>
        </div>
      ) : null}

      {result && step === "done" ? (
        <div className="rounded-xl border bg-card-white p-4">
          <p className="font-semibold text-navy-deep">
            Imported {result.created} item{result.created === 1 ? "" : "s"}
            {result.skipped > 0 ? ` · ${result.skipped} skipped` : ""}
          </p>
          {result.errors.length > 0 ? (
            <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-sm text-muted-foreground">
              {result.errors.map((item) => (
                <li key={`${item.row}-${item.equipmentId}`}>
                  Row {item.row} ({item.equipmentId}): {item.message}
                </li>
              ))}
            </ul>
          ) : null}
          <Button className="mt-4" onClick={() => { navigate("/equipment"); }}>
            View equipment
          </Button>
        </div>
      ) : null}

      {!csv && step === "upload" ? (
        <EmptyState
          icon={Upload}
          title="No CSV loaded"
          description="Download the template, fill in your fleet assets, then paste or upload here."
        />
      ) : null}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "danger" | "warning";
}) {
  return (
    <div
      className={cn(
        "rounded-xl border px-4 py-3",
        tone === "success" && "border-emerald-200 bg-emerald-50/60",
        tone === "danger" && "border-red-200 bg-red-50/60",
        tone === "warning" && "border-amber-200 bg-amber-50/60",
        tone === "default" && "bg-card-white",
      )}
    >
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-navy-deep">{value}</p>
    </div>
  );
}

function IssueList({
  errors,
  warnings,
}: {
  errors: { message: string }[];
  warnings: { message: string }[];
}) {
  if (errors.length === 0 && warnings.length === 0) {
    return <span className="text-emerald-700">Ready</span>;
  }

  return (
    <ul className="space-y-1 text-xs">
      {errors.map((issue, index) => (
        <li key={`e-${index}`} className="text-red-700">
          {issue.message}
        </li>
      ))}
      {warnings.map((issue, index) => (
        <li key={`w-${index}`} className="flex items-start gap-1 text-amber-800">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          {issue.message}
        </li>
      ))}
    </ul>
  );
}
